import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, CardHeader, Code,
  Divider, FormControl, FormHelperText, FormLabel, HStack, Heading,
  IconButton, Input, List, ListItem, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, NumberDecrementStepper,
  NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper,
  SimpleGrid, Switch, Tab, TabList, Tabs, Tag, Text, Textarea, VStack, Wrap,
  WrapItem, useToast,
} from '@chakra-ui/react';
import { AttachmentIcon, CloseIcon } from '@chakra-ui/icons';
import { Trans, useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import reminderEmailService from '../services/reminderEmailService';

// *Key fields are i18n keys — translated where rendered.
const KIND_META = {
  checkIn: {
    titleKey: 'reminderEmails.kinds.checkIn.title',
    sendBeforeKey: 'reminderEmails.kinds.checkIn.sendBefore',
    anchorInfoKey: 'reminderEmails.kinds.checkIn.anchorInfo',
    anchorField: 'hotelCheckInTime',
    blurbKey: 'reminderEmails.kinds.checkIn.blurb',
  },
  checkOut: {
    titleKey: 'reminderEmails.kinds.checkOut.title',
    sendBeforeKey: 'reminderEmails.kinds.checkOut.sendBefore',
    anchorInfoKey: 'reminderEmails.kinds.checkOut.anchorInfo',
    anchorField: 'hotelCheckOutTime',
    blurbKey: 'reminderEmails.kinds.checkOut.blurb',
  },
  guestLink: {
    titleKey: 'reminderEmails.kinds.guestLink.title',
    eventDriven: true,
    customOnly: true,
    blurbKey: 'reminderEmails.kinds.guestLink.blurb',
  },
  checkoutEmail: {
    titleKey: 'reminderEmails.kinds.checkoutEmail.title',
    eventDriven: true,
    blurbKey: 'reminderEmails.kinds.checkoutEmail.blurb',
  },
};

// "How long before" presets shown next to the minutes field.
const OFFSET_PRESETS = [
  { labelKey: 'reminderEmails.presets.oneHour', minutes: 60 },
  { labelKey: 'reminderEmails.presets.threeHours', minutes: 180 },
  { labelKey: 'reminderEmails.presets.sixHours', minutes: 360 },
  { labelKey: 'reminderEmails.presets.dayBefore', minutes: 1440 },
];

const KIND_LABEL = {
  check_in: 'reminderEmails.kindLabels.checkIn',
  check_out: 'reminderEmails.kindLabels.checkOut',
  guest_link: 'reminderEmails.kindLabels.guestLink',
  checkout_email: 'reminderEmails.kindLabels.checkoutEmail',
};

// Delivery status (API enum) -> i18n key; unknown statuses show as-is.
const STATUS_LABEL = {
  sent: 'reminderEmails.status.sent',
  skipped: 'reminderEmails.status.skipped',
  failed: 'reminderEmails.status.failed',
  pending: 'reminderEmails.status.pending',
};

const STATUS_COLOR = {
  sent: 'green',
  skipped: 'orange',
  failed: 'red',
  pending: 'gray',
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatBytes = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// "15:00" minus 180 minutes → { time: "12:00", day: '' }; day is
// 'before' / 'after' when the send time falls on another day.
const sendTimePreview = (anchorHHMM, offsetMinutes) => {
  const offset = Number(offsetMinutes);
  if (!anchorHHMM || Number.isNaN(offset)) return null;
  const [h, m] = anchorHHMM.split(':').map(Number);
  let total = h * 60 + m - offset;
  let day = '';
  while (total < 0) { total += 1440; day = 'before'; }
  while (total >= 1440) { total -= 1440; day = 'after'; }
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return { time: `${hh}:${mm}`, day };
};

const PREVIEW_KEY = {
  '': 'reminderEmails.timing.previewSameDay',
  before: 'reminderEmails.timing.previewDayBefore',
  after: 'reminderEmails.timing.previewDayAfter',
};

// Native names for the language tabs; unknown codes show upper-cased.
const LANGUAGE_NAMES = { en: 'English', lt: 'Lietuvių', fil: 'Filipino', th: 'ไทย', es: 'Español', fr: 'Français', de: 'Deutsch', it: 'Italiano', pt: 'Português', id: 'Bahasa Indonesia' };
const langName = (code, fallback) => LANGUAGE_NAMES[code] || fallback || code.toUpperCase();

// Editable per-language variants from the API payload:
// { th: { subject, bodyHtml } } for every non-default operating language.
const variantsFrom = (kindData) => Object.fromEntries(
  Object.entries(kindData?.translations || {}).map(([code, v]) => [
    code, { subject: v.subject || '', bodyHtml: v.bodyHtml || '' },
  ]),
);

const ReminderEmails = () => {
  const { t } = useTranslation();
  const kindLabel = (kind) => (KIND_LABEL[kind] ? t(KIND_LABEL[kind]) : kind);
  const statusLabel = (status) => (STATUS_LABEL[status] ? t(STATUS_LABEL[status]) : status);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyKind, setBusyKind] = useState('');
  const [sampleTo, setSampleTo] = useState({ checkIn: '', checkOut: '', guestLink: '', checkoutEmail: '' });
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  // Version history modal: { kind, revisions, loading, viewing, confirmId }.
  const [history, setHistory] = useState(null);
  // Selected language tab per email kind ('' = the hotel default language).
  const [activeLang, setActiveLang] = useState({ checkIn: '', checkOut: '', guestLink: '', checkoutEmail: '' });
  const fileInputs = { checkIn: useRef(null), checkOut: useRef(null), guestLink: useRef(null), checkoutEmail: useRef(null) };
  const toast = useToast();

  // Rebuilds BOTH server data and the editable form. Only safe when the
  // server state matches the screen (initial load, after Save) — it
  // discards unsaved edits.
  const applyResponse = (res) => {
    setData(res);
    setForm({
      checkIn: {
        enabled: res.checkIn.enabled,
        sendOffsetMinutes: res.checkIn.sendOffsetMinutes,
        subject: res.checkIn.subject,
        bodyHtml: res.checkIn.bodyHtml,
        translations: variantsFrom(res.checkIn),
      },
      checkOut: {
        enabled: res.checkOut.enabled,
        sendOffsetMinutes: res.checkOut.sendOffsetMinutes,
        subject: res.checkOut.subject,
        bodyHtml: res.checkOut.bodyHtml,
        translations: variantsFrom(res.checkOut),
      },
      guestLink: {
        enabled: res.guestLink?.enabled || false,
        subject: res.guestLink?.subject || '',
        bodyHtml: res.guestLink?.bodyHtml || '',
        translations: variantsFrom(res.guestLink),
      },
      checkoutEmail: {
        enabled: res.checkoutEmail?.enabled || false,
        subject: res.checkoutEmail?.subject || '',
        bodyHtml: res.checkoutEmail?.bodyHtml || '',
        translations: variantsFrom(res.checkoutEmail),
      },
    });
  };

  // Updates server-owned state only (attachment lists, defaults) —
  // uploads/deletes must NOT wipe subject/body the user has typed but
  // not yet saved. Rebuilding the form here was the bug: adding a
  // poster file reset it to the server's (still empty) template, so
  // the next Save submitted blank fields.
  const applyData = (res) => setData(res);

  const load = async () => {
    try {
      setLoading(true);
      applyResponse(await reminderEmailService.get());
    } catch {
      toast({ title: t('reminderEmails.toasts.loadFailed'), status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async () => {
    try {
      setActivityLoading(true);
      setActivity(await reminderEmailService.getActivity());
    } catch {
      toast({ title: t('reminderEmails.toasts.activityLoadFailed'), status: 'error' });
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (kind, field, value) =>
    setForm((prev) => ({ ...prev, [kind]: { ...prev[kind], [field]: value } }));

  // Subject/body edits go to the active language tab: the default tab
  // edits the primary text, other tabs edit translations[code].
  const setTextField = (kind, field, value) => {
    const code = activeLang[kind];
    if (!code) {
      setField(kind, field, value);
      return;
    }
    setForm((prev) => {
      const variants = prev[kind].translations || {};
      return {
        ...prev,
        [kind]: {
          ...prev[kind],
          translations: {
            ...variants,
            [code]: { ...(variants[code] || { subject: '', bodyHtml: '' }), [field]: value },
          },
        },
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await reminderEmailService.update(form);
      applyResponse(res);
      toast({ title: t('reminderEmails.toasts.saved'), status: 'success' });
    } catch (e) {
      toast({
        title: t('reminderEmails.toasts.saveFailed'),
        description: e?.response?.data?.detail || t('reminderEmails.toasts.checkValues'),
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (kind, file) => {
    if (!file) return;
    setBusyKind(kind);
    try {
      const res = await reminderEmailService.uploadAttachment(kind, file);
      applyData(res);
      toast({ title: t('reminderEmails.toasts.attached', { name: file.name }), status: 'success' });
    } catch (e) {
      toast({
        title: t('reminderEmails.toasts.uploadFailed'),
        description: e?.response?.data?.detail || t('reminderEmails.toasts.fileRejected'),
        status: 'error',
      });
    } finally {
      setBusyKind('');
      if (fileInputs[kind].current) fileInputs[kind].current.value = '';
    }
  };

  const handleDeleteAttachment = async (kind, att) => {
    setBusyKind(kind);
    try {
      const res = await reminderEmailService.deleteAttachment(att.id);
      applyData(res);
      toast({ title: t('reminderEmails.toasts.attachmentRemoved', { name: att.name }), status: 'info' });
    } catch {
      toast({ title: t('reminderEmails.toasts.attachmentRemoveFailed'), status: 'error' });
    } finally {
      setBusyKind('');
    }
  };

  const handleSendSample = async (kind) => {
    const to = (sampleTo[kind] || '').trim();
    if (!to) {
      toast({ title: t('reminderEmails.toasts.sampleNeedsEmail'), status: 'warning' });
      return;
    }
    setBusyKind(kind);
    try {
      const code = activeLang[kind];
      const text = code ? (form[kind].translations?.[code] || {}) : form[kind];
      const res = await reminderEmailService.sendSample(kind, to, {
        subject: text.subject || '',
        bodyHtml: text.bodyHtml || '',
        language: code || data.defaultLanguage || 'en',
      });
      toast({ title: res.message || t('reminderEmails.toasts.sampleSent', { to }), status: 'success' });
    } catch (e) {
      toast({
        title: t('reminderEmails.toasts.sampleFailed'),
        description: e?.response?.data?.detail || '',
        status: 'error',
      });
    } finally {
      setBusyKind('');
    }
  };

  const openHistory = async (kind) => {
    setHistory({ kind, revisions: [], loading: true, viewing: null, confirmId: null });
    try {
      const res = await reminderEmailService.getRevisions(kind);
      setHistory((h) => h && { ...h, revisions: res.revisions || [], loading: false });
    } catch {
      setHistory((h) => h && { ...h, loading: false });
      toast({ title: t('reminderEmails.toasts.historyLoadFailed'), status: 'error' });
    }
  };

  const viewRevision = async (rev) => {
    if (history?.viewing?.id === rev.id) {
      setHistory((h) => ({ ...h, viewing: null }));
      return;
    }
    try {
      const res = await reminderEmailService.getRevision(rev.id);
      setHistory((h) => ({ ...h, viewing: res }));
    } catch {
      toast({ title: t('reminderEmails.toasts.revisionLoadFailed'), status: 'error' });
    }
  };

  const restoreRevision = async (rev) => {
    setBusyKind(history.kind);
    try {
      await reminderEmailService.restoreRevision(rev.id);
      // Restored text is live on the server now; reload so the form shows it.
      applyResponse(await reminderEmailService.get());
      toast({ title: t('reminderEmails.toasts.restored'), status: 'success' });
      setHistory(null);
    } catch (e) {
      toast({ title: t('reminderEmails.toasts.restoreFailed'), description: e?.response?.data?.detail || '', status: 'error' });
    } finally {
      setBusyKind('');
    }
  };

  const copyPlaceholder = (name) => {
    const token = `{{${name}}}`;
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(token);
    toast({ title: t('reminderEmails.toasts.placeholderCopied', { token }), status: 'info', duration: 2500 });
  };

  if (loading || !form || !data) {
    return (
      <PageShell title={t('reminderEmails.page.title')} subtitle={t('reminderEmails.page.loadingSubtitle')}>
        <Text color="gray.500">{t('reminderEmails.page.loading')}</Text>
      </PageShell>
    );
  }

  // Delivery log + the actionable list: arrivals with no email address on
  // the booking can't receive anything, and that is by far the most common
  // reason a guest reports never getting their door code.
  const renderActivityCard = () => (
    <Card>
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <Heading size="md">{t('reminderEmails.activity.title')}</Heading>
          <Button size="sm" variant="outline" isLoading={activityLoading} onClick={loadActivity}>
            {activity ? t('reminderEmails.activity.refresh') : t('reminderEmails.activity.check')}
          </Button>
        </HStack>
        <Text fontSize="sm" color="gray.500" mt={1}>
          {t('reminderEmails.activity.intro')}
        </Text>
      </CardHeader>
      {activity && (
        <CardBody pt={2}>
          <VStack align="stretch" spacing={5}>
            {activity.upcomingWithoutEmail?.length > 0 && (
              <Alert status="warning" borderRadius="md" alignItems="flex-start">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold">
                    {t('reminderEmails.activity.missingEmailTitle', {
                      count: activity.upcomingWithoutEmail.length,
                    })}
                  </Text>
                  <Text fontSize="sm" mb={2}>
                    {t('reminderEmails.activity.missingEmailHelp')}
                  </Text>
                  <List spacing={1} fontSize="sm">
                    {activity.upcomingWithoutEmail.map((r) => (
                      <ListItem key={r.reservationId}>
                        <Code>{r.reservationId}</Code>{' '}
                        {t('reminderEmails.activity.missingEmailRow', {
                          name: r.guestName || t('reminderEmails.activity.guestFallback'),
                          date: r.checkInDate,
                        })}
                        {r.phone ? ` · ${r.phone}` : ''}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Alert>
            )}

            {activity.upcomingWithoutEmail?.length === 0 && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  {t('reminderEmails.activity.allHaveEmail')}
                </Text>
              </Alert>
            )}

            <Box>
              <Text fontWeight="semibold" fontSize="sm" mb={2}>
                {t('reminderEmails.activity.last30Days')}
              </Text>
              <Wrap>
                {Object.entries(activity.summary || {}).flatMap(([kind, counts]) =>
                  Object.entries(counts).map(([status, count]) => (
                    <WrapItem key={`${kind}-${status}`}>
                      <Tag colorScheme={STATUS_COLOR[status] || 'gray'} size="sm">
                        {t('reminderEmails.activity.summaryTag', {
                          kind: kindLabel(kind), n: count, status: statusLabel(status),
                        })}
                      </Tag>
                    </WrapItem>
                  )),
                )}
              </Wrap>
              {Object.keys(activity.summary || {}).length === 0 && (
                <Text fontSize="sm" color="gray.500">
                  {t('reminderEmails.activity.noneDue')}
                </Text>
              )}
            </Box>

            {activity.dispatches?.length > 0 && (
              <Box>
                <Text fontWeight="semibold" fontSize="sm" mb={2}>
                  {t('reminderEmails.activity.recent')}
                </Text>
                <VStack align="stretch" spacing={1} fontSize="sm">
                  {activity.dispatches.slice(0, 25).map((d) => (
                    <HStack
                      key={`${d.reservationId}-${d.kind}`}
                      justify="space-between"
                      borderBottomWidth="1px"
                      borderColor="gray.100"
                      pb={1}
                    >
                      <HStack spacing={2} minW={0}>
                        <Tag size="sm" colorScheme={STATUS_COLOR[d.status] || 'gray'}>
                          {statusLabel(d.status)}
                        </Tag>
                        <Code fontSize="xs">{d.reservationId}</Code>
                        <Text noOfLines={1}>{d.guestName}</Text>
                      </HStack>
                      <HStack spacing={2} flexShrink={0}>
                        <Text color="gray.500">{kindLabel(d.kind)}</Text>
                        {d.reason && (
                          <Text color="orange.600" fontSize="xs">{d.reason}</Text>
                        )}
                        <Text color="gray.400" fontSize="xs">
                          {formatWhen(d.sentAt || d.scheduledFor)}
                        </Text>
                      </HStack>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </CardBody>
      )}
    </Card>
  );

  const renderKindCard = (kind) => {
    const meta = KIND_META[kind];
    const f = form[kind];
    // Guard for a backend that predates this kind (deploy gap).
    const d = data[kind] || { attachments: [], defaultSubject: '', defaultBody: '' };
    const anchor = data[meta.anchorField];
    const preview = sendTimePreview(anchor, f.sendOffsetMinutes);
    // Language tabs (only when the hotel operates in several languages).
    const primaryLang = data.defaultLanguage || 'en';
    const otherLangs = Object.keys(d.translations || {});
    const langTabs = [primaryLang, ...otherLangs];
    const labelFor = (code) => langName(
      code, (data.languages || []).find((l) => l.code === code)?.label,
    );
    const code = activeLang[kind];
    const variantDefaults = code ? (d.translations?.[code] || {}) : null;
    const text = code ? (f.translations?.[code] || { subject: '', bodyHtml: '' }) : f;
    const defaultSubject = code ? variantDefaults.defaultSubject : d.defaultSubject;
    const usesPrimary = code && !(text.subject || '').trim() && !(text.bodyHtml || '').trim()
      && ((f.subject || '').trim() || (f.bodyHtml || '').trim());
    return (
      <Card key={kind}>
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <HStack>
              <Heading size="md">{t(meta.titleKey)}</Heading>
              <Badge colorScheme={f.enabled ? 'green' : 'gray'}>
                {meta.customOnly
                  ? (f.enabled ? t('reminderEmails.badges.customOn') : t('reminderEmails.badges.builtIn'))
                  : (f.enabled ? t('reminderEmails.badges.autoOn') : t('reminderEmails.badges.off'))}
              </Badge>
            </HStack>
            <HStack>
            <Button size="xs" variant="outline" onClick={() => openHistory(kind)}>
              {t('reminderEmails.buttons.history')}
            </Button>
            <Switch
              isChecked={f.enabled}
              onChange={(e) => setField(kind, 'enabled', e.target.checked)}
              colorScheme="green"
            />
            </HStack>
          </HStack>
          <Text fontSize="sm" color="gray.500" mt={1}>{t(meta.blurbKey)}</Text>
        </CardHeader>
        <CardBody pt={2}>
          <VStack align="stretch" spacing={4}>
            {!meta.eventDriven && (
            <FormControl>
              <FormLabel>{t(meta.sendBeforeKey)}</FormLabel>
              <HStack>
                <NumberInput
                  value={f.sendOffsetMinutes}
                  min={-1440}
                  max={10080}
                  w="140px"
                  onChange={(_, num) =>
                    setField(kind, 'sendOffsetMinutes', Number.isNaN(num) ? 0 : num)
                  }
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="sm" color="gray.600">{t('reminderEmails.timing.minutes')}</Text>
                <Wrap>
                  {OFFSET_PRESETS.map((p) => (
                    <WrapItem key={p.minutes}>
                      <Button
                        size="xs"
                        variant={f.sendOffsetMinutes === p.minutes ? 'solid' : 'outline'}
                        onClick={() => setField(kind, 'sendOffsetMinutes', p.minutes)}
                      >
                        {t(p.labelKey)}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              </HStack>
              <FormHelperText>
                {t(meta.anchorInfoKey, { time: anchor, tz: data.hotelTimezone })}
                {preview && ` ${t(PREVIEW_KEY[preview.day], { time: preview.time })}`}
              </FormHelperText>
            </FormControl>
            )}

            {langTabs.length > 1 && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>{t('reminderEmails.language.label')}</Text>
                <Tabs
                  size="sm"
                  variant="soft-rounded"
                  colorScheme="blue"
                  index={Math.max(0, langTabs.indexOf(code || primaryLang))}
                  onChange={(i) => setActiveLang((prev) => ({
                    ...prev, [kind]: i === 0 ? '' : langTabs[i],
                  }))}
                >
                  <TabList flexWrap="wrap">
                    {langTabs.map((c, i) => {
                      const filled = i === 0
                        ? ((f.subject || '').trim() || (f.bodyHtml || '').trim())
                        : ((f.translations?.[c]?.subject || '').trim()
                          || (f.translations?.[c]?.bodyHtml || '').trim());
                      return (
                        <Tab key={c}>
                          {i === 0
                            ? t('reminderEmails.language.defaultTab', { lang: labelFor(c) })
                            : labelFor(c)}
                          {filled ? ' •' : ''}
                        </Tab>
                      );
                    })}
                  </TabList>
                </Tabs>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {code
                    ? t('reminderEmails.language.variantHelp', {
                      lang: labelFor(code), primary: labelFor(primaryLang),
                    })
                    : t('reminderEmails.language.defaultHelp')}
                </Text>
                {usesPrimary && (
                  <Alert status="info" borderRadius="md" mt={2} py={2}>
                    <AlertIcon />
                    <Text fontSize="xs">
                      {t('reminderEmails.language.noVariant', {
                        lang: labelFor(code), primary: labelFor(primaryLang),
                      })}
                    </Text>
                  </Alert>
                )}
              </Box>
            )}

            <FormControl>
              <FormLabel>{t('reminderEmails.editor.subject')}</FormLabel>
              <Input
                value={text.subject || ''}
                placeholder={defaultSubject}
                onChange={(e) => setTextField(kind, 'subject', e.target.value)}
              />
              <FormHelperText>{t('reminderEmails.editor.subjectHelp')}</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>{t('reminderEmails.editor.message')}</FormLabel>
              <Textarea
                value={text.bodyHtml || ''}
                rows={12}
                fontFamily="mono"
                fontSize="sm"
                placeholder={t('reminderEmails.editor.messagePlaceholder')}
                onChange={(e) => setTextField(kind, 'bodyHtml', e.target.value)}
              />
              <FormHelperText>
                <Trans
                  i18nKey="reminderEmails.editor.messageHelp"
                  values={{ example: '{{guest_name}}' }}
                  components={{ code: <Code /> }}
                />
              </FormHelperText>
            </FormControl>

            <Divider />

            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="semibold" fontSize="sm">
                  <Trans
                    i18nKey="reminderEmails.attachments.title"
                    components={{ muted: <Text as="span" color="gray.500" /> }}
                  />
                </Text>
                <Button
                  size="sm"
                  leftIcon={<AttachmentIcon />}
                  isLoading={busyKind === kind}
                  onClick={() => fileInputs[kind].current?.click()}
                >
                  {t('reminderEmails.attachments.addFile')}
                </Button>
                <input
                  type="file"
                  hidden
                  ref={fileInputs[kind]}
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.txt"
                  onChange={(e) => handleUpload(kind, e.target.files?.[0])}
                />
              </HStack>
              {d.attachments.length === 0 ? (
                <Text fontSize="sm" color="gray.400">
                  {t('reminderEmails.attachments.empty')}
                </Text>
              ) : (
                <List spacing={1}>
                  {d.attachments.map((att) => (
                    <ListItem key={att.id}>
                      <HStack justify="space-between">
                        <HStack>
                          <AttachmentIcon color="gray.500" />
                          <Text fontSize="sm">{att.name}</Text>
                          <Text fontSize="xs" color="gray.400">{formatBytes(att.size)}</Text>
                        </HStack>
                        <IconButton
                          aria-label={t('reminderEmails.attachments.removeItem', { name: att.name })}
                          icon={<CloseIcon boxSize={2.5} />}
                          size="xs"
                          variant="ghost"
                          isDisabled={busyKind === kind}
                          onClick={() => handleDeleteAttachment(kind, att)}
                        />
                      </HStack>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            <Divider />

            <FormControl>
              <FormLabel fontSize="sm">{t('reminderEmails.sample.label')}</FormLabel>
              <HStack>
                <Input
                  size="sm"
                  type="email"
                  placeholder="you@example.com"
                  value={sampleTo[kind]}
                  onChange={(e) =>
                    setSampleTo((prev) => ({ ...prev, [kind]: e.target.value }))
                  }
                />
                <Button
                  size="sm"
                  isLoading={busyKind === kind}
                  onClick={() => handleSendSample(kind)}
                >
                  {t('reminderEmails.sample.send')}
                </Button>
              </HStack>
              <FormHelperText>
                {t('reminderEmails.sample.help')}
              </FormHelperText>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  return (
    <PageShell
      title={t('reminderEmails.page.title')}
      subtitle={t('reminderEmails.page.subtitle')}
    >
      <VStack align="stretch" spacing={5}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box fontSize="sm">
            {t('reminderEmails.page.intro', { tz: data.hotelTimezone })}
          </Box>
        </Alert>

        <Box>
          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            {t('reminderEmails.placeholders.title')}
          </Text>
          <Wrap>
            {data.placeholders.map((name) => (
              <WrapItem key={name}>
                <Tag
                  size="sm"
                  cursor="pointer"
                  onClick={() => copyPlaceholder(name)}
                  _hover={{ bg: 'blue.100' }}
                >
                  {`{{${name}}}`}
                </Tag>
              </WrapItem>
            ))}
            {(data.customPlaceholders || []).map(({ key, scope }) => (
              <WrapItem key={`custom-${key}`}>
                <Tag
                  size="sm"
                  cursor="pointer"
                  colorScheme={scope === 'room' ? 'purple' : 'teal'}
                  onClick={() => copyPlaceholder(key)}
                  _hover={{ opacity: 0.8 }}
                  title={scope === 'room'
                    ? t('reminderEmails.placeholders.perRoomTitle')
                    : t('reminderEmails.placeholders.hotelWideTitle')}
                >
                  {`{{${key}}}`}
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
          {(data.customPlaceholders || []).length > 0 && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              {t('reminderEmails.placeholders.customHelp')}
            </Text>
          )}
        </Box>

        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
          {renderKindCard('checkIn')}
          {renderKindCard('checkOut')}
          {renderKindCard('guestLink')}
          {renderKindCard('checkoutEmail')}
        </SimpleGrid>

        <HStack justify="flex-end">
          <Button colorScheme="blue" isLoading={saving} onClick={handleSave}>
            {t('reminderEmails.buttons.save')}
          </Button>
        </HStack>

        {renderActivityCard()}
      </VStack>

      <Modal isOpen={!!history} onClose={() => setHistory(null)} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('reminderEmails.history.title', {
              name: history && KIND_META[history.kind] ? t(KIND_META[history.kind].titleKey) : '',
            })}
            <Text fontSize="sm" fontWeight="normal" color="gray.500">
              {t('reminderEmails.history.intro')}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {history?.loading && <Text color="gray.500">{t('reminderEmails.page.loading')}</Text>}
            {history && !history.loading && history.revisions.length === 0 && (
              <Text color="gray.500">{t('reminderEmails.history.empty')}</Text>
            )}
            <VStack align="stretch" spacing={2}>
              {(history?.revisions || []).map((rev) => (
                <Box key={rev.id} borderWidth="1px" borderRadius="md" p={3}>
                  <HStack justify="space-between" align="flex-start" flexWrap="wrap">
                    <Box>
                      <HStack>
                        <Badge colorScheme={{ save: 'blue', restore: 'green', baseline: 'purple' }[rev.action] || 'gray'}>
                          {rev.action === 'baseline' ? t('reminderEmails.history.original') : rev.actionLabel}
                        </Badge>
                        {(rev.languages || []).map((code) => (
                          <Tag key={code} size="sm">{langName(code)}</Tag>
                        ))}
                      </HStack>
                      <Text fontSize="sm" mt={1}>
                        {formatWhen(rev.createdAt)}{rev.createdBy ? ` · ${rev.createdBy}` : ''}
                      </Text>
                      {rev.subject && (
                        <Text fontSize="sm" color="gray.600" noOfLines={1}>
                          {t('reminderEmails.history.subjectLine', { subject: rev.subject })}
                        </Text>
                      )}
                    </Box>
                    <HStack>
                      <Button size="sm" variant="outline" onClick={() => viewRevision(rev)}>
                        {history.viewing?.id === rev.id ? t('reminderEmails.buttons.hide') : t('reminderEmails.buttons.view')}
                      </Button>
                      {history.confirmId === rev.id ? (
                        <>
                          <Button size="sm" colorScheme="green" isLoading={busyKind === history.kind}
                            onClick={() => restoreRevision(rev)}>
                            {t('reminderEmails.buttons.confirmRestore')}
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => setHistory((h) => ({ ...h, confirmId: null }))}>
                            {t('reminderEmails.buttons.cancel')}
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" colorScheme="green" variant="outline"
                          onClick={() => setHistory((h) => ({ ...h, confirmId: rev.id }))}>
                          {t('reminderEmails.buttons.restore')}
                        </Button>
                      )}
                    </HStack>
                  </HStack>
                  {history.viewing?.id === rev.id && (
                    <Box as="pre" mt={3} p={3} bg="gray.50" borderRadius="md" fontSize="xs"
                      maxH="320px" overflow="auto" whiteSpace="pre-wrap" wordBreak="break-word">
                      {history.viewing.content || t('reminderEmails.history.blankMessage')}
                      {Object.entries(history.viewing.extra?.translations || {}).map(([code, v]) => (
                        `\n\n── ${langName(code)} ──\n${t('reminderEmails.history.subjectLine', {
                          subject: v.subject || t('reminderEmails.history.defaultSubject'),
                        })}\n${v.body_html || t('reminderEmails.history.defaultMessage')}`
                      )).join('')}
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setHistory(null)}>{t('reminderEmails.buttons.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
};

export default ReminderEmails;
